import type { Translations } from '../types';

export const es: Translations = {
  common: {
    home: 'Inicio',
    github: 'GitHub',
    copy: 'Copiar',
    clear: 'Limpiar',
    input: 'Entrada',
    output: 'Salida',
    encode: 'Codificar',
    decode: 'Decodificar',
    generate: 'Generar',
    convert: 'Convertir',
    result: 'Resultado',
    copiedToClipboard: '¡Copiado al portapapeles!',
    failedToCopy: 'Error al copiar',
    language: 'Idioma',
  },
  seo: {
    siteDescription: 'Colección gratuita de herramientas para desarrolladores centradas en la privacidad. Formateador JSON, codificador/decodificador Base64, generador UUID, generador hash, codificador URL, convertidor de timestamp y convertidor de colores. Todas las herramientas funcionan sin conexión en tu navegador.',
    keywords: [
      'herramientas para desarrolladores',
      'formateador JSON',
      'codificador Base64',
      'generador UUID',
      'generador hash',
      'codificador URL',
      'convertidor timestamp',
      'convertidor color',
      'herramientas en línea',
      'herramientas gratis',
      'privacidad',
      'herramientas sin conexión',
    ],
    ogTitle: 'GetDev.Tools - Herramientas gratuitas para desarrolladores centradas en la privacidad',
    ogDescription: 'Colección de herramientas gratuitas para desarrolladores que funcionan sin conexión en tu navegador. Formateador JSON, Base64, UUID, Hash, codificador URL y más.',
  },
  home: {
    title: 'Colección de herramientas para desarrolladores',
    subtitle: 'Herramientas gratuitas y centradas en la privacidad para desarrolladores. Todo el procesamiento ocurre en su navegador.',
    whyTitle: '¿Por qué GetDev.Tools?',
    privacyTitle: '🔒 Privacidad primero',
    privacyDesc: 'Todo el procesamiento ocurre en su navegador. Sus datos nunca salen de su dispositivo.',
    fastTitle: '⚡ Ultrarrápido',
    fastDesc: 'Sin viajes al servidor. Todo se ejecuta localmente para resultados instantáneos.',
    freeTitle: '🆓 Gratis para siempre',
    freeDesc: 'Código abierto y gratuito. No requiere registro ni pago.',
    footer: '© 2025 GetDev.Tools - Herramientas para desarrolladores centradas en la privacidad',
    footerSubtitle: 'Todo el procesamiento ocurre en su navegador',
  },
  tools: {
    jsonFormatter: {
      name: 'Formateador JSON',
      description: 'Formatear, validar y embellecer datos JSON',
      format: 'Formatear',
      minify: 'Minificar',
      validate: 'Validar',
      error: 'Error',
    },
    base64: {
      name: 'Codificador/Decodificador Base64',
      description: 'Codificar y decodificar cadenas Base64',
      encodeButton: 'Codificar',
      decodeButton: 'Decodificar',
    },
    uuid: {
      name: 'Generador UUID',
      description: 'Generar identificadores UUID v4 conformes a RFC 4122',
      numberOfUuids: 'Número de UUIDs a generar',
      generatedUuids: 'UUIDs generados',
      copyAll: 'Copiar todo',
      aboutTitle: 'Acerca de UUID v4',
      aboutDesc: 'Los UUID (Identificadores Únicos Universales) v4 son identificadores de 128 bits generados aleatoriamente. La probabilidad de colisión es extremadamente baja, lo que los hace adecuados para sistemas distribuidos sin coordinación central.',
    },
    hash: {
      name: 'Generador de hash',
      description: 'Generar hashes SHA-1, SHA-256, SHA-512',
      inputText: 'Texto de entrada',
      securityNotice: '⚠️ Aviso de seguridad',
      securityDesc: 'SHA-1 está obsoleto por razones de seguridad. Use SHA-256 o SHA-512 para aplicaciones criptográficas. MD5 no está disponible en navegadores modernos debido a vulnerabilidades de seguridad.',
    },
    urlEncoder: {
      name: 'Codificador/Decodificador URL',
      description: 'Codificar y decodificar cadenas URL',
      encodeUrl: 'Codificar URL',
      encodeComponent: 'Codificar componente',
      encoded: 'Codificado',
      decoded: 'Decodificado',
      differenceTitle: '💡 Diferencia',
      encodeUriDesc: 'encodeURI: Codifica una URI completa (no codifica: ; , / ? : @ & = + $ #)',
      encodeComponentDesc: 'encodeURIComponent: Codifica componente URI (codifica todos los caracteres especiales)',
      examples: 'Ejemplos',
    },
    timestamp: {
      name: 'Convertidor de marca de tiempo',
      description: 'Convertir entre marcas de tiempo Unix y fechas legibles',
      currentTime: 'Hora actual',
      unixMs: 'Marca de tiempo Unix (ms)',
      unixS: 'Marca de tiempo Unix (s)',
      toDate: 'Marca de tiempo → Fecha',
      toTimestamp: 'Fecha → Marca de tiempo',
      useNow: 'Usar ahora',
      aboutTitle: '💡 Acerca de las marcas de tiempo Unix',
      aboutDesc1: '• Marca de tiempo Unix = segundos desde el 1 de enero de 1970 00:00:00 UTC (época)',
      aboutDesc2: '• 10 dígitos = segundos (ej: 1609459200)',
      aboutDesc3: '• 13 dígitos = milisegundos (ej: 1609459200000)',
      aboutDesc4: '• Formatos de fecha admitidos: ISO 8601, RFC 2822 o lenguaje natural',
    },
    color: {
      name: 'Convertidor de color',
      description: 'Convertir entre formatos HEX, RGB y HSL',
      pickColor: 'Elegir un color',
      formatGuide: '💡 Guía de formatos de color',
      hexDesc: 'HEX: Notación hexadecimal (#RRGGBB)',
      rgbDesc: 'RGB: Valores rojo, verde, azul (0-255)',
      hslDesc: 'HSL: Matiz (0-360°), Saturación (0-100%), Luminosidad (0-100%)',
      alphaDesc: 'RGBA/HSLA: Igual que RGB/HSL con canal Alfa (opacidad)',
    },
    jwt: { name: 'Decodificador JWT', description: 'Decodificar e inspeccionar JSON Web Tokens', inputToken: 'Token JWT', header: 'Encabezado', payload: 'Carga útil', signature: 'Firma (Base64)', claims: 'Claims estándar', invalidFormat: 'Formato JWT inválido', invalidToken: 'Token JWT inválido', securityNotice: 'Aviso de seguridad', securityDesc: 'Esta herramienta solo decodifica tokens JWT', aboutTitle: 'Acerca de JWT', aboutDesc1: 'JWT = JSON Web Token', aboutDesc2: 'Formato: encabezado.carga.firma', aboutDesc3: 'Usado para autenticación segura' },
    diff: { name: 'Comparación de texto', description: 'Comparar dos bloques de texto', originalText: 'Texto original', modifiedText: 'Texto modificado', sideBySide: 'Lado a lado', unified: 'Unificado', noDifference: 'Sin diferencias' },
    markdown: { name: 'Vista previa Markdown', description: 'Escribir y previsualizar Markdown en tiempo real', editor: 'Editor', preview: 'Vista previa', supportedFeatures: 'Soportado: Encabezados, Listas, Enlaces, Imágenes, Bloques de código, Tablas, etc.' },
    regex: { name: 'Probador Regex', description: 'Probar expresiones regulares', pattern: 'Expresión regular', flags: 'Banderas', testString: 'Cadena de prueba', matches: 'Coincidencias', noMatches: 'Sin coincidencias', invalidRegex: 'Regex inválida', globalFlag: 'Global (g)', caseInsensitive: 'Insensible a mayúsculas (i)', multiline: 'Multilínea (m)', dotAll: 'Punto coincide con nueva línea (s)' },
    qrCode: { name: 'Generador de código QR', description: 'Generar códigos QR para URL, texto, WiFi, etc.', inputData: 'Datos de entrada', qrCodeImage: 'Imagen del código QR', size: 'Tamaño (píxeles)', errorCorrection: 'Corrección de errores', errorLow: 'Bajo (~7%)', errorMedium: 'Medio (~15%)', errorQuartile: 'Alto (~25%)', errorHigh: 'Muy alto (~30%)', templates: 'Plantillas rápidas', templateUrl: 'URL del sitio web', templateEmail: 'Correo electrónico', templatePhone: 'Número de teléfono', templateSms: 'SMS', templateWifi: 'Red WiFi', templateVcard: 'Tarjeta de contacto (vCard)', privacyNotice: '🔒 Aviso de privacidad', privacyDesc: 'Los códigos QR se generan usando una API externa (qrserver.com). Sus datos se enviarán a su servidor.' },
    lorem: { name: 'Generador Lorem Ipsum', description: 'Generar texto de relleno para diseños y prototipos', generateType: 'Tipo de generación', paragraphs: 'Párrafos', sentences: 'Oraciones', words: 'Palabras', count: 'Cantidad', startWithLorem: 'Comenzar con "Lorem ipsum"', generatedText: 'Texto generado', aboutTitle: '💡 Acerca de Lorem Ipsum', aboutDesc: 'Lorem Ipsum es un texto de relleno comúnmente usado en diseño y publicación.' },
    htmlEntity: { name: 'Codificador de entidades HTML', description: 'Codificar y decodificar entidades HTML', inputText: 'Texto de entrada', encodeBasic: 'Codificar (Básico)', encodeAll: 'Codificar todo', decode: 'Decodificar', commonEntities: 'Entidades HTML comunes', character: 'Carácter', entity: 'Entidad', description2: 'Descripción', useCases: '💡 Casos de uso', useCase1: 'Codificar Básico: Solo codifica caracteres esenciales (<, >, &, ", \')', useCase2: 'Codificar todo: Codifica todos los caracteres especiales para máxima seguridad', useCase3: 'Decodificar: Convierte entidades HTML a caracteres normales' },
  },
};

